import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const SiteConfigContext = createContext({
    layout: "classic", siteLayout: "classic", pinEnabled: false, ready: false,
    easyMode: false, setEasyMode: () => {}, refresh: () => {},
});

const EASY_KEY = "force_easy_mode";

// Fetches public, secret-free site config (incl. the admin-selected site-wide
// layout) once at boot. The admin Layout tab calls refresh() to apply changes live.
// Easy Mode is a per-device override on TOP of the admin layout — unless the admin
// forced "easy" site-wide, in which case everyone gets it.
export function SiteConfigProvider({ children }) {
    const [siteLayout, setSiteLayout] = useState("classic");
    const [pinEnabled, setPinEnabled] = useState(false);
    const [ready, setReady] = useState(false);
    const [easyMode, setEasyModeState] = useState(() => localStorage.getItem(EASY_KEY) === "1");
    const [phoneLine, setPhoneLine] = useState({ enabled: false, number: null });
    const [heroVideo, setHeroVideo] = useState({ enabled: false, url: null });

    const setEasyMode = useCallback((v) => {
        localStorage.setItem(EASY_KEY, v ? "1" : "0");
        setEasyModeState(v);
    }, []);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/config");
            setSiteLayout(data?.layout || "classic");
            setPinEnabled(!!data?.pin_enabled);
            setPhoneLine(data?.phone_line_enabled ? { enabled: true, number: data.phone_line_number } : { enabled: false, number: null });
            setHeroVideo(data?.hero_video || { enabled: false, url: null });
        } catch (e) {
            setSiteLayout("classic");
        } finally {
            setReady(true);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const layout = easyMode || siteLayout === "easy" ? "easy" : siteLayout;

    return (
        <SiteConfigContext.Provider value={{ layout, siteLayout, pinEnabled, ready, easyMode, setEasyMode, refresh, phoneLine, heroVideo }}>
            {children}
        </SiteConfigContext.Provider>
    );
}

export function useSiteConfig() {
    return useContext(SiteConfigContext);
}
